/**
 * Hook multi-lega: le leghe dell'utente, la lega corrente, cambio lega,
 * creazione, ingresso con codice, codice di ingresso.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { type User, useAuthStore } from "../lib/auth-store";

export interface LegaOut {
  id: string;
  nome: string;
  slug: string;
  stato: string;
  is_default: boolean;
  created_at: string;
}

export interface MembroLegaOut {
  ruolo_lega: string;
  ruoli_aggiuntivi: string[];
  is_fondatore: boolean;
  attivo: boolean;
}

export interface LegaMia {
  lega: LegaOut;
  membro: MembroLegaOut;
  /** Andrea 2026-10-10: ordine nel menu, preferita, uscita dalla lega */
  ordine?: number | null;
  preferita?: boolean;
  puo_abbandonare?: boolean;
  motivo_blocco_abbandono?: string | null;
}

export interface LegaCorrente {
  lega: LegaOut;
  membro: MembroLegaOut | null;
  n_membri: number;
  n_squadre: number;
  n_squadre_libere: number;
  n_stagioni: number;
  leghe_utente: LegaMia[];
  codice_invito: string | null;
}

export interface CreaLegaPayload {
  nome: string;
  modalita: "mantra" | "classic";
  anno: string;
  giornata_serie_a_inizio: number;
  giornata_serie_a_fine: number;
  budget_stagionale_milioni: number;
  budget_invernale_milioni: number;
  n_squadre: number;
  nome_squadra_fondatore: string | null;
}

export interface LegaCreata {
  lega: LegaOut;
  codice_invito: string;
  stagione_id: string;
  anno: string;
  squadra_id: string;
  n_squadre: number;
}

export interface EntraLegaOut {
  lega: LegaOut;
  membro: MembroLegaOut;
  squadra_id: string | null;
  squadra_nome: string | null;
}

export function useLegheMie(enabled = true) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["leghe-mie"],
    queryFn: async () => (await api.get<LegaMia[]>("/leghe/mie")).data,
    enabled: isAuth && enabled,
  });
}

export function useLegaCorrente() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["lega-corrente"],
    queryFn: async () => (await api.get<LegaCorrente>("/leghe/corrente")).data,
    enabled: isAuth,
  });
}

/**
 * Cambia la lega corrente: imposta l'header X-Lega-Id, ricarica il profilo
 * (ruoli della nuova lega), svuota la cache e naviga.
 */
export function useCambiaLega() {
  const setLegaId = useAuthStore((s) => s.setLegaId);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (legaId: string, to = "/") => {
    setLegaId(legaId);
    const me = await api.get<User>("/auth/me");
    setUser(me.data);
    qc.clear();
    navigate(to, { replace: true });
  };
}

/**
 * Andrea 2026-10-10: gestione delle proprie leghe dal menu in alto —
 * ordine, lega preferita (si parte da li' aprendo l'app), uscita.
 */
export function useGestioneLeghe() {
  const qc = useQueryClient();
  const setLegaId = useAuthStore((s) => s.setLegaId);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const salva = (d: LegaMia[]) => qc.setQueryData(["leghe-mie"], d);

  const ordina = useMutation({
    mutationFn: async (legaIds: string[]) =>
      (await api.put<LegaMia[]>("/leghe/mie/ordine", { lega_ids: legaIds })).data,
    // l'ordine nuovo si vede subito, il server conferma
    onMutate: (legaIds) => {
      const prima = qc.getQueryData<LegaMia[]>(["leghe-mie"]);
      if (prima) {
        const per = new Map(prima.map((l) => [l.lega.id, l]));
        salva(legaIds.map((id) => per.get(id)).filter((l): l is LegaMia => !!l));
      }
      return { prima };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prima) salva(ctx.prima);
    },
    onSuccess: salva,
  });

  const preferita = useMutation({
    mutationFn: async (v: { legaId: string; preferita: boolean }) =>
      (
        await api.put<LegaMia[]>(`/leghe/mie/${v.legaId}/preferita`, {
          preferita: v.preferita,
        })
      ).data,
    onSuccess: (d) => {
      salva(d);
      const user = useAuthStore.getState().user;
      if (user) {
        setUser({ ...user, lega_preferita_id: d.find((l) => l.preferita)?.lega.id ?? null });
      }
    },
  });

  const abbandona = useMutation({
    mutationFn: async (legaId: string) =>
      (await api.post<LegaMia[]>(`/leghe/mie/${legaId}/abbandona`)).data,
    onSuccess: async (d, legaId) => {
      const corrente = useAuthStore.getState().user?.lega_corrente?.id;
      if (legaId === corrente) {
        // uscito dalla lega in cui stava: si torna alla preferita / prima
        setLegaId(null);
        const me = await api.get<User>("/auth/me");
        setUser(me.data);
        qc.clear();
        navigate("/", { replace: true });
        return;
      }
      salva(d);
      const me = await api.get<User>("/auth/me");
      setUser(me.data);
    },
  });

  return { ordina, preferita, abbandona };
}

export function useCreaLega() {
  return useMutation({
    mutationFn: async (payload: CreaLegaPayload) =>
      (await api.post<LegaCreata>("/leghe", payload)).data,
  });
}

export function useEntraLega() {
  return useMutation({
    mutationFn: async (payload: { codice: string; nome_squadra: string | null }) =>
      (await api.post<EntraLegaOut>("/leghe/entra", payload)).data,
  });
}

export function useRigeneraCodice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ codice_invito: string }>("/leghe/corrente/codice")).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["lega-corrente"] }),
  });
}

/** Anno fanta corrente nel formato della lega ("2026-27"): da luglio conta l'anno nuovo. */
export function annoFantaCorrente(oggi = new Date()): string {
  const y = oggi.getMonth() >= 6 ? oggi.getFullYear() : oggi.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
}
